import { useTranslations } from 'next-intl';
import {
  ORDER_STATUS_DEFAULT_FILTERS,
  ORDER_STATUS_HEADING_SORTING_ORDER,
  ORDER_STATUS_LIST
} from '../constants';
import { Row } from '@cat-ecom/pcc-components/src/components/notification-drawer/types';
import { findCalRolesFilters } from '../utils/utils';

interface OrderStatus {
  [key: string]: Row[];
}

const groupBy = (items, key) =>
  items.reduce(
    (result, item) => ({
      ...result,

      [item[key]]: [...(result[item[key]] || []), item]
    }),

    {}
  );

const useOrderStatusList = ({
  userAttributes,
  calRoles = [],
  isPCCQuoteNotificationsFilteringFlag
}: {
  userAttributes: Array<{ name: string; value: string }>;
  calRoles?: string[];
  isPCCQuoteNotificationsFilteringFlag?: boolean;
}) => {
  const t = useTranslations();

  const calFilters = isPCCQuoteNotificationsFilteringFlag
    ? findCalRolesFilters(calRoles)
    : findCalRolesFilters(calRoles).filter(
        (item) =>
          !['QUOTE_PENDING_APPROVAL', 'QUOTE_REJECTED'].includes(item.label)
      );

  const orderStatusList = userAttributes.filter((attribute) =>
    attribute.name.endsWith('_Web')
  );

  const response = orderStatusList
    .filter((item) => Boolean(item?.value) && ORDER_STATUS_LIST[item.name])
    .map((item) => ({
      ...item,
      title: ORDER_STATUS_LIST[item.name]?.title || '',
      label: ORDER_STATUS_LIST[item.name]?.label,
      statusIndex: ORDER_STATUS_LIST[item.name]?.statusIndex
    }));

  const responseWithDefaults = [
    ...response,
    ...ORDER_STATUS_DEFAULT_FILTERS,
    ...calFilters
  ];

  const translateOption = ({
    name,
    value,
    title,
    label,
    ...rest
  }): { label: string; value: string; title: string; orderIndex: number } => ({
    label: t(label),
    value: label,
    title: t(title),
    orderIndex: ORDER_STATUS_HEADING_SORTING_ORDER[title],
    ...rest
  });

  responseWithDefaults.sort((a, b) => a.statusIndex - b.statusIndex);

  const orderStatusGrouped = groupBy(
    responseWithDefaults.map(translateOption),
    'title'
  );

  const sortedOrderStatus = Object.keys(orderStatusGrouped)
    .sort((a, b) => {
      const orderIndexA = orderStatusGrouped[a][0].orderIndex;
      const orderIndexB = orderStatusGrouped[b][0].orderIndex;
      return orderIndexA - orderIndexB;
    })
    .reduce((acc, curr) => {
      acc[curr] = orderStatusGrouped[curr];
      return acc;
    }, {} as OrderStatus);

  return sortedOrderStatus;
};

export default useOrderStatusList;
